import { safeStorageGet, safeStorageSet } from '../storage/safe-storage.js';

const STORAGE_KEY = 'adminsr_context';

/** @type {import('../../contracts/context.js').AppContext} */
const DEFAULT_CONTEXT = { country: 'sr', lang: 'sr' };

/**
 * Create a context manager for country/language selection.
 *
 * @param {{ isValid?: (ctx: import('../../contracts/context.js').AppContext) => boolean }} [opts]
 *   `isValid` validates a stored context against the current option set. A
 *   stored value that fails validation (e.g. a legacy country/lang removed from
 *   the options) is discarded in favour of the default — preventing a blank
 *   switcher label and an unresolvable context for returning users. Defaults to
 *   accepting any context (backward-compatible).
 * @returns {import('../../contracts/context.js').ContextManager}
 */
export function createContextManager({ isValid } = {}) {
  /** @type {unknown} */
  const stored = safeStorageGet(STORAGE_KEY, DEFAULT_CONTEXT);

  /** @type {import('../../contracts/context.js').AppContext} */
  // Shape guard first: safeStorageGet can return any valid JSON (null, string,
  // array, …), so confirm the stored value is a well-formed context before
  // trusting it or handing it to isValid — otherwise reading .country/.lang off
  // null/a primitive would throw before we could fall back.
  let current = isContextShape(stored) ? stored : DEFAULT_CONTEXT;

  // Discard a well-formed context that is no longer a valid option (legacy).
  if (isValid && !isValid(current)) {
    current = DEFAULT_CONTEXT;
  }

  /** @type {Set<import('../../contracts/context.js').ContextListener>} */
  const listeners = new Set();

  function get() {
    return { ...current };
  }

  /**
   * @param {import('../../contracts/context.js').AppContext} ctx
   */
  function set(ctx) {
    // No-op if values unchanged
    if (ctx.country === current.country && ctx.lang === current.lang) return;

    current = { country: ctx.country, lang: ctx.lang };
    safeStorageSet(STORAGE_KEY, current);

    for (const listener of listeners) {
      listener(get());
    }
  }

  /**
   * @param {import('../../contracts/context.js').ContextListener} listener
   * @returns {() => void}
   */
  function subscribe(listener) {
    listeners.add(listener);
    return () => { listeners.delete(listener); };
  }

  function destroy() {
    listeners.clear();
  }

  return { get, set, subscribe, destroy };
}

/**
 * @param {unknown} v
 * @returns {v is import('../../contracts/context.js').AppContext}
 */
function isContextShape(v) {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (/** @type {any} */ (v).country) === 'string' &&
    typeof (/** @type {any} */ (v).lang) === 'string'
  );
}
